<template>
  <PaginaAdministrar
    titulo="Produtos e Serviços"
    subtitulo="Gestão centralizada do catálogo de produtos/Serviços - Master Data"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="podeVerAcoes"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-busca" class="form-label">Busca Livre (SKU, Nome)</label>
          <input
            type="text"
            id="filtro-busca"
            class="form-control"
            v-model="filtros.busca"
            placeholder="Digite para buscar..."
            @keydown.enter.prevent="aplicarFiltros"
          >
        </div>

        <div class="col-md-3">
          <label for="filtro-tipo" class="form-label">Tipo de Produto</label>
          <select id="filtro-tipo" class="form-select" v-model="filtros.idTipoProduto">
            <option value="">Todos os Tipos</option>
            <option v-for="tipo in listas.tiposProduto" :key="tipo.idTipoProduto" :value="tipo.idTipoProduto">
              {{ tipo.descricaoBreve }}
            </option>
          </select>
        </div>

        <div class="col-md-2">
          <label for="filtro-status" class="form-label">Status</label>
          <select id="filtro-status" class="form-select" v-model="filtros.lstStatus">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaClienteItem || s.idListaSistemaItem" :value="s.idListaClienteItem || s.idListaSistemaItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>

        <div class="col-md-12">
          <label class="form-label d-block">Atributos Operacionais</label>
          <div class="d-flex flex-wrap gap-3 mt-1">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-comerc" v-model="filtros.comercializavel">
              <label class="form-check-label" for="f-comerc">Comercializável</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-comp" v-model="filtros.compravel">
              <label class="form-check-label" for="f-comp">Comprável</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="f-est" v-model="filtros.controlaEstoque">
              <label class="form-check-label" for="f-est">Controla Estoque</label>
            </div>
          </div>
        </div>

        <div class="col-12 mt-3 d-flex justify-content-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum produto cadastrado."
      row-key="idProduto"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editar($event.row)"
      modulo-layout="Produtos"
      :colunas-padrao="colunasPadrao"
      :colunas-fixas="['acoes']"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Produtos e Serviços"
      :filtros-exportacao="descricaoFiltros"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
    >
      <template #sku="{ row }">
        <span class="text-muted fw-medium">#{{ row.sku }}</span>
      </template>

      <template #nome="{ row }">
        <div class="text-truncate fw-medium text-body-emphasis" :title="row.nome" style="max-width: 300px;">{{ row.nome }}</div>
      </template>

      <template #tipo="{ row }">
        <span class="badge bg-secondary">{{ row.nomeTipoProduto }}</span>
      </template>

      <template #ncm="{ row }">
        <span class="font-monospace small">{{ row.codigoNcm }}</span>
      </template>

      <template #unidade="{ row }">
        {{ getUnidadeTexto(row.lstUnidadeMedidaBase) }}
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #descricaoTipoProduto="{ row }">
        <span class="text-muted small text-truncate d-inline-block" :title="row.descricaoTipoProduto" style="max-width: 200px;">
          {{ row.descricaoTipoProduto || '-' }}
        </span>
      </template>

      <template #lstNaturezaItem="{ row }">
        <span class="badge bg-info bg-opacity-10 text-info-emphasis">{{ getNaturezaTexto(row.lstNaturezaItem) }}</span>
      </template>

      <template #permiteConversaoVariavel="{ row }">
        <span v-if="row.permiteConversaoVariavel" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #comercializavel="{ row }">
        <span v-if="row.comercializavel" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #compravel="{ row }">
        <span v-if="row.compravel" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #controlaEstoque="{ row }">
        <span v-if="row.controlaEstoque" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #controlaLote="{ row }">
        <span v-if="row.controlaLote" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #gerirEstoquePorUnidadeLogistica="{ row }">
        <span v-if="row.gerirEstoquePorUnidadeLogistica" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" @click="editar(row)" title="Editar Produto">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>

      <!-- ========================================================= -->
      <!-- SLOTS DOS CAMPOS EXPANDIDOS                                -->
      <!-- ========================================================= -->
      <template #codigoBarras="{ row }">
        <span class="font-monospace small">{{ row.codigoBarras || '-' }}</span>
      </template>

      <template #codigoOrigemMercadoria="{ row }">
        <span class="text-muted small">{{ row.codigoOrigemMercadoria || '-' }}</span>
      </template>

      <template #descricaoOrigemMercadoria="{ row }">
        <span class="text-muted small text-truncate d-inline-block" :title="row.descricaoOrigemMercadoria" style="max-width: 200px;">
          {{ row.descricaoOrigemMercadoria || '-' }}
        </span>
      </template>

      <template #codigoReferenciaMercado="{ row }">
        <span class="small">{{ row.codigoReferenciaMercado || '-' }}</span>
      </template>

      <template #codigoSistemaAntigo="{ row }">
        <span class="text-muted small">{{ row.codigoSistemaAntigo || '-' }}</span>
      </template>

      <template #pesoBruto="{ row }">
        <span class="text-end d-block small">{{ formatarPeso(row.pesoBruto) }}</span>
      </template>

      <template #pesoLiquido="{ row }">
        <span class="text-end d-block small">{{ formatarPeso(row.pesoLiquido) }}</span>
      </template>

      <template #cubagem="{ row }">
        <span class="text-end d-block small">{{ formatarPeso(row.cubagem) }}</span>
      </template>

      <template #categoriaFinanceiraEntradaNome="{ row }">
        <span class="small">{{ row.categoriaFinanceiraEntradaNome || '-' }}</span>
      </template>

      <template #categoriaFinanceiraSaidaNome="{ row }">
        <span class="small">{{ row.categoriaFinanceiraSaidaNome || '-' }}</span>
      </template>

      <template #lstCodigoLc116="{ row }">
        <span class="font-monospace small">{{ row.lstCodigoLc116 || '-' }}</span>
      </template>

      <template #lstCodigoNbs="{ row }">
        <span class="font-monospace small">{{ row.lstCodigoNbs || '-' }}</span>
      </template>

      <template #reterIssPadrao="{ row }">
        <span v-if="row.reterIssPadrao" class="text-success"><i class="bi bi-check-lg"></i></span>
        <span v-else class="text-muted"><i class="bi bi-dash-lg"></i></span>
      </template>

      <template #nomeUsuarioCriacao="{ row }">
        <span class="small">{{ row.nomeUsuarioCriacao || '-' }}</span>
      </template>

      <template #nomeUsuarioModificacao="{ row }">
        <span class="small">{{ row.nomeUsuarioModificacao || '-' }}</span>
      </template>

      <template #tsCriacao="{ row }">
        <span class="small">{{ formatarData(row.tsCriacao) }}</span>
      </template>

      <template #tsModificacao="{ row }">
        <span class="small">{{ formatarData(row.tsModificacao) }}</span>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Produto"
    :chave-primaria="auditoriaSelecionada.id"
    :tabelas-filhas="['ProdutoAtributo', 'ProdutoValorAtributoNcm', 'ProdutoConversaoUnidades', 'ProdutoFornecedor']"
    chave-estrangeira="IdProduto"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{
      'lstStatus': listas.status,
      'status': listas.status,
      'idTipoProduto': listas.tiposProduto,
      'tipoProduto': listas.tiposProduto,
      'lstUnidadeMedidaBase': listas.unidades,
      'lstNaturezaItem': listas.naturezas
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, computed, nextTick, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const formatarBooleano = (val) => val ? 'Sim' : 'Não'

const formatarData = (val) => {
  if (!val) return '-'
  try {
    return new Date(val).toLocaleString('pt-BR')
  } catch { return val }
}

const formatarPeso = (val) => {
  if (val == null) return '-'
  return Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}

const colunasPadrao = [
  { field: 'sku', label: 'SKU', width: 140, sortable: true },
  { field: 'nome', label: 'Produto', width: 250, sortable: true },
  { field: 'nomeTipoProduto', label: 'Tipo', width: 150, sortable: true },
  { field: 'descricaoTipoProduto', label: 'Descrição Tipo', width: 200, sortable: false },
  { field: 'codigoNcm', label: 'NCM', width: 120, sortable: true, align: 'center' },
  {
    field: 'lstUnidadeMedidaBase',
    label: 'UN',
    width: 80,
    sortable: true,
    align: 'center',
    formatter: (val) => getUnidadeTexto(val)
  },
  {
    field: 'lstStatus',
    label: 'Status',
    width: 120,
    sortable: true,
    align: 'center',
    formatter: (val) => getStatusTexto(val)
  },
  {
    field: 'lstNaturezaItem',
    label: 'Natureza Item',
    width: 140,
    sortable: true,
    align: 'center',
    formatter: (val) => getNaturezaTexto(val)
  },
  {
    field: 'permiteConversaoVariavel',
    label: 'Conv. Variável',
    width: 120,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  {
    field: 'comercializavel',
    label: 'Comercializável',
    width: 130,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  {
    field: 'compravel',
    label: 'Comprável',
    width: 110,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  {
    field: 'controlaEstoque',
    label: 'Ctrl. Estoque',
    width: 120,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  {
    field: 'controlaLote',
    label: 'Ctrl. Lote',
    width: 110,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  {
    field: 'gerirEstoquePorUnidadeLogistica',
    label: 'Gestão Unid. Log.',
    width: 140,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },

  // =========================================================
  // CAMPOS EXPANDIDOS
  // =========================================================
  { field: 'codigoBarras', label: 'Cód. Barras', width: 130, sortable: true },
  { field: 'codigoOrigemMercadoria', label: 'Origem Merc.', width: 110, sortable: true, align: 'center' },
  { field: 'descricaoOrigemMercadoria', label: 'Desc. Origem', width: 160, sortable: false },
  { field: 'codigoReferenciaMercado', label: 'Ref. Mercado', width: 140, sortable: true },
  { field: 'codigoSistemaAntigo', label: 'Cód. Antigo', width: 130, sortable: true },
  {
    field: 'pesoBruto',
    label: 'Peso Bruto',
    width: 110,
    sortable: true,
    align: 'right',
    formatter: formatarPeso
  },
  {
    field: 'pesoLiquido',
    label: 'Peso Líq.',
    width: 110,
    sortable: true,
    align: 'right',
    formatter: formatarPeso
  },
  {
    field: 'cubagem',
    label: 'Cubagem',
    width: 100,
    sortable: true,
    align: 'right',
    formatter: formatarPeso
  },
  { field: 'categoriaFinanceiraEntradaNome', label: 'Cat. Fin. Entrada', width: 200, sortable: false },
  { field: 'categoriaFinanceiraSaidaNome', label: 'Cat. Fin. Saída', width: 200, sortable: false },
  { field: 'lstCodigoLc116', label: 'LC 116', width: 100, sortable: true, align: 'center' },
  { field: 'lstCodigoNbs', label: 'NBS', width: 100, sortable: true, align: 'center' },
  {
    field: 'reterIssPadrao',
    label: 'Reter ISS',
    width: 100,
    sortable: true,
    align: 'center',
    formatter: formatarBooleano
  },
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 150, sortable: false },
  { field: 'nomeUsuarioModificacao', label: 'Modificado por', width: 150, sortable: false },
  {
    field: 'tsCriacao',
    label: 'Data Criação',
    width: 150,
    sortable: true,
    formatter: formatarData
  },
  {
    field: 'tsModificacao',
    label: 'Data Modif.',
    width: 150,
    sortable: true,
    formatter: formatarData
  }
]

const colunas = computed(() => [
  ...colunasPadrao.map(c => {
    // Mapeia fields cujo slotName difere do nome do campo
    const slotMap = {
      nomeTipoProduto: 'tipo',
      codigoNcm: 'ncm',
      lstUnidadeMedidaBase: 'unidade',
      lstStatus: 'status'
    }
    return { ...c, slotName: slotMap[c.field] || c.field }
  }),
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatus: (row) => getStatusTexto(row.lstStatus),
    lstUnidadeMedidaBase: (row) => getUnidadeTexto(row.lstUnidadeMedidaBase),
    lstNaturezaItem: (row) => getNaturezaTexto(row.lstNaturezaItem)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({
  tiposProduto: [],
  status: [],
  unidades: [],
  naturezas: []
})

const filtros = reactive({
  busca: '',
  idTipoProduto: '',
  lstStatus: '',
  comercializavel: false,
  compravel: false,
  controlaEstoque: false
})

// Descrição textual dos filtros ativos para incluir na exportação
const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.busca) partes.push(`Busca: "${filtros.busca}"`)
  if (filtros.idTipoProduto) {
    const tipo = listas.tiposProduto?.find(t => t.idTipoProduto === filtros.idTipoProduto)
    partes.push(`Tipo: ${tipo?.descricaoBreve || filtros.idTipoProduto}`)
  }
  if (filtros.lstStatus) {
    const status = listas.status?.find(s => s.idListaSistemaItem === filtros.lstStatus || s.idListaClienteItem === filtros.lstStatus)
    partes.push(`Status: ${status?.textoLista || filtros.lstStatus}`)
  }
  if (filtros.comercializavel) partes.push('Comercializável')
  if (filtros.compravel) partes.push('Comprável')
  if (filtros.controlaEstoque) partes.push('Controla Estoque')
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

const podeVerAcoes = computed(() => true)

const emitNotification = (p) => emit('show-notification', p)

// ============================================================
// Estado da exportação
// ============================================================
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const carregarListas = async () => {
  try {
    const [resStatus, resTipos, resUnidades, resNaturezas] = await Promise.all([
      api.get('/ListasCliente/lookup/ST'),
      api.get('/TiposProduto?pageSize=1000'),
      api.get('/ListasCliente/lookup/UN_MED'),
      api.get('/ListasCliente/lookup/PROD_NATUREZA')
    ])
    listas.status = resStatus.data
    listas.tiposProduto = Array.isArray(resTipos.data) ? resTipos.data : (resTipos.data?.items || [])
    listas.unidades = resUnidades.data
    listas.naturezas = resNaturezas.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares do filtro:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus.replace('ST_', '')
}

const getUnidadeTexto = (idUnidade) => {
  if (!idUnidade) return '-'
  const obj = listas.unidades.find(u => u.idListaSistemaItem === idUnidade || u.idListaClienteItem === idUnidade)
  return obj ? obj.textoLista : idUnidade
}

const getNaturezaTexto = (idNatureza) => {
  if (!idNatureza) return '-'
  const obj = listas.naturezas.find(n => n.idListaSistemaItem === idNatureza || n.idListaClienteItem === idNatureza)
  return obj ? obj.textoLista : idNatureza
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página para listar os produtos.'
    lista.value = []
    paginacao.value.TotalCount = 0
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) {
      params.append('idEmpresa', authStore.empresaAtiva)
    }

    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.idTipoProduto) params.append('idTipoProduto', filtros.idTipoProduto)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

    if (filtros.comercializavel) params.append('comercializavel', 'true')
    if (filtros.compravel) params.append('compravel', 'true')
    if (filtros.controlaEstoque) params.append('controlaEstoque', 'true')

    const response = await api.get(`/Produtos?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar produtos:', error)
    erro.value = 'Não foi possível carregar os dados dos produtos.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }

const limparFiltros = () => {
  filtros.busca = ''
  filtros.idTipoProduto = ''
  filtros.lstStatus = ''
  filtros.comercializavel = false
  filtros.compravel = false
  filtros.controlaEstoque = false
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const novoItem = () => { emit('adicionar-item') }
const editar = (item) => { emit('editar-item', item) }
const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idProduto
  auditoriaSelecionada.nome = `${item.sku} - ${item.nome}`
  mostrarAuditoria.value = true
}

// ============================================================
// Exportação de dados (AdminTable)
// ============================================================
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  const totalRegistros = paginacao.value.totalCount ?? paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.idTipoProduto) params.append('idTipoProduto', filtros.idTipoProduto)
      if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
      if (filtros.comercializavel) params.append('comercializavel', 'true')
      if (filtros.compravel) params.append('compravel', 'true')
      if (filtros.controlaEstoque) params.append('controlaEstoque', 'true')

      const response = await api.get(`/Produtos?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]

    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[ProdutosAdministrar] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.tiposProduto = []
    listas.status = []
    listas.unidades = []
  }
})

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<style scoped>
.filtros-titulo { font-size: 1.1rem; font-weight: 600; color: #495057; margin-bottom: 1rem; }
[data-bs-theme="dark"] .filtros-titulo { color: #e9ecef; }
</style>
